import { useState, useEffect, useMemo, useCallback } from 'react';
import { X, AlertCircle, Minus } from 'lucide-react';
import { Input, Button, Checkbox, Alert, AlertDescription } from '@predoc/cow';
import { ColumnDef } from '@tanstack/react-table';
import { useDebounce } from '@/hooks';
import { useListProfiles } from '@/feature/profiles/hooks/profilesAPI';
import { ProfileListItem } from '@/feature/profiles/types';
import { SelectedProfile } from '../types';
import {
  Pagination,
  PaginationState,
  ProfileBadge,
  DataTable,
  TableSkeleton,
} from '@/components';

interface ProfileSelectionTableProps {
  selectedProfiles: SelectedProfile[];
  onSelectionChange: (profiles: SelectedProfile[]) => void;
  validationError?: string;
}

// Empty state component defined outside to prevent unnecessary rerenders
// Added padding-top to prevent image cutoff - DataTable renders with p-0, so padding is inside
const EmptyState = () => (
  <div className="flex min-h-[300px] flex-col items-center justify-center px-4 pt-8 pb-12">
    <img
      src="/suggested-providers.png"
      alt="No profiles found"
      className="mb-4 max-w-[200px]"
    />
    <h3 className="text-lg font-semibold">No Profiles Found</h3>
    <p className="text-muted-foreground text-sm">Try adjusting your search.</p>
  </div>
);

export const ProfileSelectionTable = ({
  selectedProfiles,
  onSelectionChange,
  validationError,
}: ProfileSelectionTableProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: 5,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Reset to first page when search changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [debouncedSearchTerm]);

  const { data: profilesData, isLoading } = useListProfiles({
    searchTerms: debouncedSearchTerm ? [debouncedSearchTerm] : undefined,
    pagination: {
      offset: pagination.page * pagination.pageSize,
      limit: pagination.pageSize,
    },
  });

  const profiles: ProfileListItem[] = profilesData?.data || [];
  const totalCount = profilesData?.totalCount || 0;

  const selectedIds = useMemo(
    () => new Set(selectedProfiles.map((p) => p.id)),
    [selectedProfiles],
  );

  const allVisibleSelected = useMemo(() => {
    if (profiles.length === 0) return false;
    return profiles.every((profile) => selectedIds.has(profile.id));
  }, [profiles, selectedIds]);

  const someVisibleSelected = useMemo(() => {
    if (profiles.length === 0) return false;
    const selectedCount = profiles.filter((profile) =>
      selectedIds.has(profile.id),
    ).length;
    return selectedCount > 0 && selectedCount < profiles.length;
  }, [profiles, selectedIds]);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        // Add all visible profiles to selection
        const newProfiles = profiles.filter(
          (profile) => !selectedIds.has(profile.id),
        );
        const profilesToAdd: SelectedProfile[] = newProfiles.map((profile) => ({
          id: profile.id,
          name: profile.displayName,
          patientCount: profile.patientCount || 0,
          color: profile.avatarType,
        }));
        onSelectionChange([...selectedProfiles, ...profilesToAdd]);
      } else {
        // Remove all visible profiles from selection
        const visibleIds = new Set(profiles.map((p) => p.id));
        onSelectionChange(
          selectedProfiles.filter((p) => !visibleIds.has(p.id)),
        );
      }
    },
    [profiles, selectedIds, selectedProfiles, onSelectionChange],
  );

  const handleSelectProfile = useCallback(
    (profile: SelectedProfile, checked: boolean) => {
      if (checked) {
        onSelectionChange([...selectedProfiles, profile]);
      } else {
        onSelectionChange(selectedProfiles.filter((p) => p.id !== profile.id));
      }
    },
    [selectedProfiles, onSelectionChange],
  );

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Column definitions for DataTable
  const columns: ColumnDef<ProfileListItem>[] = useMemo(
    () => [
      {
        id: 'select',
        meta: {
          className: '!min-w-0 !pr-1',
        },
        header: () => (
          <div className="w-8">
            {someVisibleSelected && !allVisibleSelected ? (
              <div
                className="border-primary bg-primary flex h-4 w-4 cursor-pointer items-center justify-center rounded-sm border"
                onClick={() => handleSelectAll(false)}
                role="checkbox"
                aria-checked="mixed"
                aria-label="Select all profiles"
              >
                <Minus className="text-primary-foreground h-3 w-3" />
              </div>
            ) : (
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={handleSelectAll}
                aria-label="Select all profiles"
                className="checkbox-clean"
              />
            )}
          </div>
        ),
        cell: ({ row }) => {
          const profile = row.original;
          const isSelected = selectedIds.has(profile.id);
          const selectedProfile: SelectedProfile = {
            id: profile.id,
            name: profile.displayName,
            patientCount: profile.patientCount || 0,
            color: profile.avatarType,
          };

          return (
            <div>
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked: boolean) =>
                  handleSelectProfile(selectedProfile, checked)
                }
                aria-label={`Select ${profile.displayName}`}
                className="checkbox-clean"
              />
            </div>
          );
        },
        enableSorting: false,
        size: 40,
      },
      {
        accessorKey: 'displayName',
        meta: {
          className: '!pl-1',
        },
        enableSorting: false,
        header: () => (
          <span className="text-muted-foreground font-normal">Profile</span>
        ),
        cell: ({ row }) => (
          <ProfileBadge
            name={row.original.displayName}
            color={row.original.avatarType}
          />
        ),
      },
      {
        accessorKey: 'patientCount',
        enableSorting: false,
        header: () => (
          <span className="text-muted-foreground font-normal">
            Patient Count
          </span>
        ),
        cell: ({ row }) => (
          <span className="font-normal">{row.original.patientCount || 0}</span>
        ),
      },
    ],
    [
      allVisibleSelected,
      someVisibleSelected,
      selectedIds,
      handleSelectAll,
      handleSelectProfile,
    ],
  );

  const totalPatientCount = useMemo(
    () => selectedProfiles.reduce((sum, p) => sum + p.patientCount, 0),
    [selectedProfiles],
  );

  return (
    <div>
      <div className="mb-4">
        <label className="mb-1 block text-sm font-normal">Profile Search</label>
        <div className="relative w-1/2">
          <Input
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="ex. Charlotte Site"
            className="pr-8"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearch}
              className="absolute top-1/2 right-1 h-6 w-6 -translate-y-1/2 p-0"
            >
              <X className="size-3" />
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={pagination.pageSize} columns={3} />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={profiles}
            emptyState={<EmptyState />}
          />

          <div className="mt-4 mb-8 flex items-center justify-between">
            <span className="text-muted-foreground text-sm">
              ({selectedProfiles.length}) profiles, ({totalPatientCount})
              patients selected
            </span>
            <Pagination
              pagination={pagination}
              setPagination={setPagination}
              totalCount={totalCount}
              pageSizeOptions={[5, 10, 20]}
            />
          </div>

          {validationError ? (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="size-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          ) : null}
        </>
      )}
    </div>
  );
};
